import React from 'react';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  brandFonts,
  fontSize,
  visuallyHidden,
} from '../styles';
import { Button } from './ui';
import Review from './Review';

const StyledTripAdvisorReviews = styled.section`
  ${({ display }) => {
    if (!display) {
      return css`
        ${visuallyHidden()};
      `;
    }
  }}
`;

const StyledSummaries = styled.aside`
  margin-top: 25px;
  border: 1px solid
    ${({ altTheme }) =>
      altTheme ? brandColours.primary : greyColours.lightGrey};

  ${minBreakpointQuery.small`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.mlarge`
    grid-template-columns: repeat(3, 1fr);
    margin-top: 40px;
  `}
`;

const StyledSummary = styled.div`
  padding: 30px;
  border: 0 solid
    ${({ altTheme }) =>
      altTheme ? brandColours.primary : greyColours.lightGrey};
  border-top-width: 1px;

  ${minBreakpointQuery.small`
    border-top-width: 0;
  `}

  ${minBreakpointQuery.mlarge`
    border-left-width: 1px;
  `}

  &:first-child {
    border-top-width: 0;

    ${minBreakpointQuery.small`
      grid-column: 1/3;
      border-bottom-width: 1px;
    `}

    ${minBreakpointQuery.mlarge`
      grid-column: auto;
      border-bottom-width: 0;
      border-left-width: 0;
    `}
  }

  &:last-child {
    ${minBreakpointQuery.small`
      border-left-width: 1px;
    `}
  }
`;

const StyledHeading = styled.h3`
  ${brandFonts.secondary};
  ${fontSize(30)};
  line-height: 1.2;

  ${minBreakpointQuery.tiny`
    ${fontSize(33)};
  `}

  ${minBreakpointQuery.small`
    ${fontSize(36)};
  `}
`;

const StyledOverallRatingBadge = styled.img`
  margin-top: 20px;

  ${({ altTheme }) => {
    if (altTheme) {
      return css`
        padding: 10px;
        background-color: ${standardColours.white};
        border-radius: 2px;
      `;
    }
  }}
`;

const StyledText = styled.p`
  margin-top: 20px;
  ${fontSize(14)};
`;

const StyledList = styled.ul`
  margin-top: 20px;
`;

const StyledListItem = styled.li`
  display: flex;
  align-items: center;
  margin-top: 10px;
  white-space: nowrap;

  &:first-child {
    margin-top: 0;
  }

  ${({ alt }) => {
    if (alt) {
      return css`
        justify-content: space-between;
      `;
    }
  }}
`;

const StyledRatingBadge = styled.img`
  margin-right: 10px;
`;

const StyledRatingBar = styled.progress`
  margin-left: 10px;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  appearance: none;
`;

const StyledItems = styled.div``;

const StyledButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 30px;

  ${minBreakpointQuery.small`
    margin-top: 35px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
  `}
`;

const StyledButton = styled(Button)`
  color: ${brandColours.primary};
  background-color: ${brandColours.septenary};

  span {
    &:after {
      color: ${brandColours.primary};
    }
  }
`;

const TripAdvisorReviews = ({ id, summary, items, display, altTheme }) => {
  const formatDimensionName = name => {
    if (name === 'sleepQuality') {
      return 'Sleep Quality';
    } else {
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
  };

  const getRatingName = name => {
    if (name === 'fiveStar') {
      return 'Excellent';
    } else if (name === 'fourStar') {
      return 'Very Good';
    } else if (name === 'threeStar') {
      return 'Average';
    } else if (name === 'twoStar') {
      return 'Poor';
    } else if (name === 'oneStar') {
      return 'Terrible';
    }
  };

  return (
    <StyledTripAdvisorReviews display={display}>
      <StyledSummaries altTheme={altTheme}>
        <StyledSummary altTheme={altTheme}>
          <StyledHeading>TripAdvisor Rating</StyledHeading>
          {summary.ratingImageUrl ? (
            <StyledOverallRatingBadge
              src={summary.ratingImageUrl}
              alt={`TripAdvisor Overall Rating ${summary.overallRating} / 5`}
              loading="lazy"
              altTheme={altTheme}
            />
          ) : (
            <StyledText>
              TripAdvisor Overall Rating {summary.overallRating} / 5
            </StyledText>
          )}
          <StyledText>Based on {summary.numberOfReviews} reviews</StyledText>
        </StyledSummary>
        <StyledSummary altTheme={altTheme}>
          <StyledHeading>What to Expect</StyledHeading>
          <StyledList>
            {summary.dimensions.map(({ 0: name, 1: rating }, id) => (
              <StyledListItem key={id}>
                <StyledRatingBadge
                  src={`https://static.tacdn.com/img2/ratings/traveler/ss${
                    rating % 1 === 0 ? `${rating}.0` : rating
                  }.svg`}
                  alt={rating}
                  loading="lazy"
                />
                {formatDimensionName(name)}
              </StyledListItem>
            ))}
          </StyledList>
        </StyledSummary>
        <StyledSummary altTheme={altTheme}>
          <StyledHeading>Traveller Rating</StyledHeading>
          <StyledList>
            {summary.ratings.map(({ 0: name, 1: rating }, id) => (
              <StyledListItem key={id} alt={true}>
                {getRatingName(name)} ({rating})
                <StyledRatingBar value={rating} max={summary.numberOfReviews}>
                  {rating}
                </StyledRatingBar>
              </StyledListItem>
            ))}
          </StyledList>
        </StyledSummary>
      </StyledSummaries>
      <StyledItems>
        {items.map(
          ({ title, rating, ratingImageUrl, review, reviewUrl }, id) => (
            <Review
              key={id}
              heading={title}
              tripAdvisorRating={{
                rating: rating,
                imageUrl: ratingImageUrl,
              }}
              quote={`<p>${review}... <a href="${reviewUrl}" target="_blank" rel="noreferrer noopener">Read more</a></p>`}
              altTheme={altTheme}
            />
          )
        )}
      </StyledItems>
      <StyledButtons>
        <StyledButton
          href={`https://www.tripadvisor.com/Hotel_Review-d${id}`}
          target="_blank"
          rel="noopener noreferrer"
          arrow={true}
        >
          Read all reviews
        </StyledButton>
        <StyledButton
          href={`https://www.tripadvisor.com/UserReviewEdit-d${id}`}
          target="_blank"
          rel="noopener noreferrer"
          arrow={true}
        >
          Write a review
        </StyledButton>
      </StyledButtons>
    </StyledTripAdvisorReviews>
  );
};

export default TripAdvisorReviews;
